import { useEffect, useRef, useState, type RefObject } from 'react';
import { BOOT, buildBootParticles, drawBoot, sampleWord, settleAt, type BootParticle } from '../utils/bootField';
import { fallbackAnchor, findAnnaAnchor, type AnchorGeometry } from '../utils/annaAnchor';
import { REST_SPIN } from '../utils/particleField';
import { hexToRgb } from '../utils/color';

export type BootPhase = 'word' | 'morph' | 'ready' | 'done';

const MORPH_SPIN = 0.9;

/**
 * Drives the "anna → orb" boot sequence. The orb forms on top of the real avatar of the current
 * screen (its position and size are re-measured while the page settles), slows to her resting spin,
 * and reports where it sits so the overlay copy can line up with the page's own caption.
 */
export function useBootAnimation(canvasRef: RefObject<HTMLCanvasElement>, accent: string, reduced: boolean) {
  const [phase, setPhase] = useState<BootPhase>(reduced ? 'ready' : 'word');
  const [anchor, setAnchor] = useState<AnchorGeometry | null>(null);
  const accentRef = useRef(accent);
  accentRef.current = accent;

  useEffect(() => {
    if (reduced) {
      setAnchor(findAnnaAnchor() ?? fallbackAnchor(window.innerWidth, window.innerHeight));
      const id = window.setTimeout(() => setPhase('done'), 1600);
      return () => window.clearTimeout(id);
    }
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    let raf = 0;
    let particles: BootParticle[] = [];
    let w = 0;
    let h = 0;
    let start = 0;
    let last = 0;
    let angle = 0;
    let frame = 0;
    let lastPhase: BootPhase = 'word';
    let cancelled = false;
    const target: AnchorGeometry = { cx: 0, cy: 0, R: 0 };

    const layout = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const measure = (snap: boolean) => {
      const a = findAnnaAnchor() ?? fallbackAnchor(w, h);
      const k = snap ? 1 : 0.25;
      target.cx += (a.cx - target.cx) * k;
      target.cy += (a.cy - target.cy) * k;
      target.R += (a.R - target.R) * k;
    };

    const begin = () => {
      if (cancelled) return;
      layout();
      measure(true);
      setAnchor({ ...target });
      // The word is written large, centre stage; the orb then forms wherever Anna lives on this screen.
      const fontPx = Math.min(w * 0.24, h * 0.32, 300);
      particles = buildBootParticles(sampleWord('anna', w, h, fontPx, w / 2, h * 0.46));
      start = performance.now();
      last = start;
      raf = requestAnimationFrame(loop);
    };

    const loop = (now: number) => {
      const t = (now - start) / 1000;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      // Re-measure while the page beneath settles (fonts, viewport, layout).
      if (++frame % 10 === 0) {
        measure(false);
        if (frame % 30 === 0) setAnchor({ ...target });
      }
      angle += dt * (MORPH_SPIN + (REST_SPIN - MORPH_SPIN) * settleAt(t));
      drawBoot({ ctx, w, h, t, angle, cx: target.cx, cy: target.cy, R: target.R, accent: hexToRgb(accentRef.current), particles });
      const next: BootPhase = t < BOOT.morphStart ? 'word' : t < BOOT.settled ? 'morph' : t < BOOT.done ? 'ready' : 'done';
      if (next !== lastPhase) {
        lastPhase = next;
        if (next === 'ready') setAnchor({ ...target });
        setPhase(next);
      }
      if (next !== 'done') raf = requestAnimationFrame(loop);
    };

    // Wait for the display face so the word is sampled in Outfit, not a fallback.
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (fonts?.load) fonts.load('200 120px Outfit').then(begin, begin);
    else begin();

    const onResize = () => {
      layout();
      measure(true);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [canvasRef, reduced]);

  return { phase, anchor, skip: () => setPhase('done') };
}
